// src/utils/boardUtils.ts
// FIXED VERSION - Creates both squares array AND piecesMap with CONSISTENT key format
import { Position, Board, Piece, PieceColor } from '../types/Board.ts';

export function isValidPosition(pos: Position, board: Board): boolean {
  return (
    pos.row >= 0 &&
    pos.row < board.dimensions.rows &&
    pos.col >= 0 &&
    pos.col < board.dimensions.cols
  );
}

export function getPieceAt(board: Board, pos: Position): Piece | null {
  if (!isValidPosition(pos, board)) return null;
  return board.squares[pos.row][pos.col];
}

export function setPieceAt(board: Board, pos: Position, piece: Piece | null): void {
  if (!isValidPosition(pos, board)) {
    throw new Error(`Invalid position: (${pos.row}, ${pos.col})`);
  }
  board.squares[pos.row][pos.col] = piece;
  if (piece) {
    piece.position = { ...pos };
  }
  
  // ALSO UPDATE PIECESMAP IF IT EXISTS - FIXED: Use dash separator
  if ((board as any).piecesMap instanceof Map) {
    const key = `${pos.row}-${pos.col}`;
    if (piece === null) {
      (board as any).piecesMap.delete(key);
    } else {
      (board as any).piecesMap.set(key, piece);
    }
  }
}

export function positionsEqual(a: Position, b: Position): boolean {
  return a.row === b.row && a.col === b.col;
}

export function positionToString(pos: Position): string {
  return `${pos.row}-${pos.col}`;  // FIXED: Use dash separator
}

export function stringToPosition(str: string): Position {
  const [row, col] = str.split('-').map(Number);  // FIXED: Split on dash
  return { row, col };
}

export function createEmptyBoard(rows: number, cols: number, variantId: string): Board {
  const squares: (Piece | null)[][] = [];
  for (let row = 0; row < rows; row++) {
    squares[row] = [];
    for (let col = 0; col < cols; col++) {
      squares[row][col] = null;
    }
  }

  // Create piecesMap for compatibility
  const piecesMap = new Map<string, Piece>();

  return {
    dimensions: { rows, cols },
    squares,
    piecesMap,  // Include piecesMap
    metadata: {
      variantId,
      createdAt: new Date()
    }
  } as any;
}

export function cloneBoard(board: Board): Board {
  // Clone the squares array deeply
  const squares = board.squares.map(row => 
    row.map(piece => {
      if (!piece) return null;
      
      return {
        ...piece, 
        position: { ...piece.position },
        rules: {
          ...piece.rules,
          movements: piece.rules.movements.map(m => ({
            ...m,
            directions: m.directions.map(d => ({...d}))
          })),
          capturePattern: Array.isArray(piece.rules.capturePattern)
            ? piece.rules.capturePattern.map(cp => ({
                ...cp,
                directions: cp.directions.map(d => ({...d}))
              }))
            : piece.rules.capturePattern
        }
      };
    })
  );

  // *** FIX: CREATE PIECESMAP FROM SQUARES with CONSISTENT key format ***
  // This ensures compatibility with code that expects piecesMap
  const piecesMap = new Map<string, Piece>();
  for (let row = 0; row < squares.length; row++) {
    for (let col = 0; col < squares[row].length; col++) {
      const piece = squares[row][col];
      if (piece) {
        const key = `${row}-${col}`;  // FIXED: Use dash separator
        piecesMap.set(key, piece);
      }
    }
  }

  return {
    dimensions: { ...board.dimensions },
    squares,
    piecesMap,  // *** CRITICAL: Include piecesMap ***
    metadata: { ...board.metadata }
  } as any;  // Use 'as any' to bypass strict type checking
}

export function getPositionsBetween(from: Position, to: Position): Position[] {
  const positions: Position[] = [];
  const rowDiff = to.row - from.row;
  const colDiff = to.col - from.col;
  
  const rowStep = rowDiff === 0 ? 0 : rowDiff / Math.abs(rowDiff);
  const colStep = colDiff === 0 ? 0 : colDiff / Math.abs(colDiff);
  
  let currentRow = from.row + rowStep;
  let currentCol = from.col + colStep;
  
  while (currentRow !== to.row || currentCol !== to.col) {
    positions.push({ row: currentRow, col: currentCol });
    currentRow += rowStep;
    currentCol += colStep;
  }
  
  return positions;
}

export function isPathClear(board: Board, from: Position, to: Position): boolean {
  const between = getPositionsBetween(from, to);
  return between.every(pos => getPieceAt(board, pos) === null);
}

export function getAllPieces(board: Board, color?: PieceColor): Piece[] {
  const pieces: Piece[] = [];
  
  for (let row = 0; row < board.dimensions.rows; row++) {
    for (let col = 0; col < board.dimensions.cols; col++) {
      const piece = board.squares[row][col];
      if (piece && (!color || piece.color === color)) {
        pieces.push(piece);
      }
    }
  }
  
  return pieces;
}

export function findKing(board: Board, color: PieceColor): Piece | null {
  for (let row = 0; row < board.dimensions.rows; row++) {
    for (let col = 0; col < board.dimensions.cols; col++) {
      const piece = board.squares[row][col];
      // Check for both 'king' and 'emperor' types as checkmate pieces
      if (piece && piece.color === color && 
          (piece.type.toLowerCase() === 'king' || piece.type.toLowerCase() === 'emperor')) {
        return piece;
      }
    }
  }
  return null;
}

export function getOpponentColor(color: PieceColor): PieceColor {
  return color === 'white' ? 'black' : 'white';
}